// src/page/LawsuitDetail.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import TrustedFormCapture from "../components/TrustedFormCapture";
import { lawsuits } from "../data/lawsuits";
import { formConfigs, defaultFormConfigKey } from "../data/formConfigs";

const INSERT_URL = "https://masstortplaintiffs.com/api/insert.php";

function slugToTitle(s) {
  return (s || "")
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

export default function LawsuitDetail() {
  const { slug } = useParams();

  // form steps
  const [step, setStep] = useState("quiz");
  const contactFormRef = useRef(null);

  // form states
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  // page source
  const [pageSource, setPageSource] = useState("");
  useEffect(() => {
    setPageSource(window.location.href);
  }, [slug]);

  // load case data
  const found = lawsuits.find((x) => x.slug === slug);
  const data = found || {
    slug,
    title: slugToTitle(slug),
    summary: "",
    heroImg: "/tort/default.jpg",
    highlights: [],
    gallery: [],
    eligibility: [],
    overview: [],
    disqualifiers: [],
    faqs: [],
  };

  const title = data.title || "Case Details";
  const summary = data.summary || "";
  const heroImg = data.heroImg || "/tort/default.jpg";

  const eligibility = data.eligibility || [];
  const overview = data.overview || [];
  const disqualifiers = data.disqualifiers || [];
  const faqs = data.faqs || [];
  const gallery = data.gallery?.slice(0, 2) || [];

  const COMMON_OVERVIEW = [
    "Mass tort vs. class action — quick guide:",
    "Mass tort: many individual cases proceed together against a common defendant. Each person keeps their own case, evidence, and potential outcome.",
    "Class action: one representative case stands for an entire group. Results apply to all class members alike (often a uniform distribution).",
    "Which is right? It depends on the type of harm, evidence available, and how damages differ person-to-person. We’ll explain the best path for your situation.",
  ];

  const COMMON_FAQ = {
    q: "Mass tort vs. class action — what’s the difference?",
    a:
      "Mass torts group similar individual cases, but each person keeps their own claim and potential recovery. " +
      "Class actions use one representative case and any result typically applies uniformly to all class members. " +
      "We’ll advise which approach fits your facts, deadlines, and evidence.",
  };

  const extendedOverview = [...overview, ...COMMON_OVERVIEW];
  const allFaqs = [...faqs, COMMON_FAQ];

  const glance = (
    eligibility.length ? eligibility.slice(0, 6) : overview.slice(0, 6)
  ).filter(Boolean);

  // form config
  const cfg = formConfigs[slug] || formConfigs[defaultFormConfigKey];

  const renderField = (f) => {
    if (f.type === "radio") {
      const cols =
        f.options.length === 3
          ? "grid-cols-3"
          : f.options.length >= 4
          ? "grid-cols-2 sm:grid-cols-4"
          : "grid-cols-2";
      return (
        <div key={f.name}>
          <p className="text-sm font-medium text-[#0A2540]">{f.label}</p>
          <div className={`mt-3 grid gap-3 ${cols}`}>
            {f.options.map((opt) => (
              <label key={opt.value || opt} className="cursor-pointer">
                <input
                  type="radio"
                  name={f.name}
                  value={opt.value || opt}
                  className="peer sr-only"
                  required={!!f.required}
                />
                <span className="flex h-12 items-center justify-center rounded-md border border-slate-300 bg-white font-semibold text-[#0A2540] peer-checked:bg-[#0A2540] peer-checked:text-white transition">
                  {opt.label || opt}
                </span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (f.type === "select") {
      return (
        <div key={f.name}>
          <label className="block text-sm font-medium text-[#0A2540] mb-1">
            {f.label}
          </label>
          <select
            name={f.name}
            required={!!f.required}
            defaultValue={f.placeholder ? "" : undefined}
            className="w-full rounded-md border border-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#C89B3C] focus:border-[#C89B3C]"
          >
            {f.placeholder && (
              <option value="" disabled>
                {f.placeholder}
              </option>
            )}
            {f.options.map((opt) => {
              const val = opt.value || opt;
              const label = opt.label || opt;
              return (
                <option key={val} value={val}>
                  {label}
                </option>
              );
            })}
          </select>
        </div>
      );
    }

    return (
      <div key={f.name}>
        <label className="block text-sm font-medium text-[#0A2540] mb-1">
          {f.label}
        </label>
        <input
          type={f.type === "number" ? "number" : "text"}
          name={f.name}
          min={f.min}
          max={f.max}
          placeholder={f.placeholder}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C89B3C] focus:border-[#C89B3C]"
          required={!!f.required}
        />
      </div>
    );
  };

  // === MAIN SUBMISSION to insert.php ===
  const submitContact = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const fd = new FormData(contactFormRef.current);

      // Normalize names for backend
      fd.append("first_name", fd.get("First name") || "");
      fd.append("last_name", fd.get("Last name") || "");
      fd.append("phone", fd.get("Phone") || "");
      fd.append("email", fd.get("Email") || "");
      fd.append("tort_type", fd.get("Case type") || title);
      fd.append("page_url", fd.get("page source") || pageSource);
      fd.append("form_type", "LawsuitDetailContact");

      const res = await fetch(INSERT_URL, {
        method: "POST",
        body: fd,
      });

      const text = await res.text();
      console.log("insert.php →", text);

      if (text.includes("success") || text.includes("Lead inserted")) {
        setSucceeded(true);
        setStep("done");
      } else {
        alert("Insert Error: " + text);
      }
    } catch (err) {
      alert("Network Error: " + err.message);
    }

    setSubmitting(false);
  };

  return (
    <>
      <main className="py-8">
      {/* GLOBAL TF — starts recording immediately */}
      <input type="hidden" id="page_trusted_cert" />
      <input type="hidden" id="page_trusted_token" />
      <input type="hidden" id="page_trusted_ping" />

      <TrustedFormCapture
        certField="page_trusted_cert"
        tokenField="page_trusted_token"
        pingField="page_trusted_ping"
      />
      
        {/* HERO */}
        <section className="mx-auto max-w-screen-2xl px-6">
          <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden">
            <img
              src={heroImg}
              alt={title}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
              <h1 className="text-white text-3xl md:text-5xl font-bold">
                {title}
              </h1>
              {summary && (
                <p className="mt-2 max-w-2xl text-white/90 text-sm md:text-base">
                  {summary}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* INTRO */}
        <section className="mx-auto max-w-screen-2xl px-6 mt-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white/80 backdrop-blur p-5">
              <h3 className="text-lg font-semibold text-[#0A2540]">
                At a glance
              </h3>
              {glance.length > 0 ? (
                <ul className="mt-3 space-y-2 text-slate-700 text-sm">
                  {glance.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-slate-700 text-sm">Key details appear here.</p>
              )}
            </div>

            <div className="md:col-span-2 grid gap-4 sm:grid-cols-2">
              {(gallery.length ? gallery : [heroImg, heroImg]).slice(0, 2).map((src, i) => (
                <div key={i} className="h-48 md:h-56 rounded-xl overflow-hidden">
                  <img
                    src={src}
                    alt={`${title}-${i}`}
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MAIN */}
        <section className="mx-auto max-w-screen-2xl px-6 mt-10">
          <div className="grid gap-10 md:grid-cols-2">
            
            {/* LEFT CONTENT */}
            <div className="space-y-10">
              {eligibility.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold text-[#0A2540]">Who may qualify</h2>
                  <ul className="mt-3 list-disc pl-6 text-slate-700">
                    {eligibility.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}

              {extendedOverview.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold text-[#0A2540]">What to know</h2>
                  <div className="mt-3 space-y-3 text-slate-700">
                    {extendedOverview.map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                </div>
              )}

              {disqualifiers.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-[#0A2540]">May not qualify if</h3>
                  <ul className="mt-3 list-disc pl-6 text-slate-700">
                    {disqualifiers.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </div>
              )}

              {allFaqs.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold text-[#0A2540]">FAQs</h2>
                  <div className="mt-4 divide-y divide-slate-200 border border-slate-200 rounded-xl">
                    {allFaqs.map((f, i) => (
                      <details key={i} className="group p-5">
                        <summary className="cursor-pointer font-medium text-[#0A2540]">{f.q}</summary>
                        <p className="mt-3 text-slate-700">{f.a}</p>
                      </details>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT FORM CARD */}
            <div className="md:sticky md:top-6 h-fit">
              <div className="rounded-2xl border border-[#E7DCC7] bg-[#F8F5EE] p-5 shadow-sm">

                <h3 className="text-2xl md:text-3xl font-extrabold text-[#0A2540]">Start Your Free Review</h3>
                <p className="text-sm mt-1 text-slate-600">Answer a few quick questions—no obligation.</p>

                {step === "done" ? (
                  
                  <div className="mt-4 text-center">
                    <p className="text-green-700 font-medium">Thank you — your details were submitted.</p>
                    <div className="mt-4 flex items-center justify-center gap-3">
                      <a href="/" className="rounded-full bg-green-600 text-white px-5 py-3 text-sm font-semibold">Back to Home</a>
                      <a href="/#active-lawsuits" className="rounded-full border border-green-600 text-green-700 px-5 py-3 text-sm font-semibold hover:bg-green-600 hover:text-white transition">Active Lawsuits</a>
                    </div>
                  </div>

                ) : step === "contact" ? (

                  <form ref={contactFormRef} onSubmit={submitContact} className="mt-4">
                    <div className="rounded-lg overflow-hidden border border-slate-200">
                      <div className="bg-[#FAF7F0] p-4 md:p-5 space-y-5">

                        <div className="text-center">
                          <p className="text-base font-semibold text-[#0A2540]">Almost there — tell us how to reach you</p>
                          <p className="text-sm text-slate-600">Confidential. No upfront fees.</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-[#0A2540] mb-1">
                              First name <span className="text-red-600">*</span>
                            </label>
                            <input
                              className="w-full rounded-md border border-slate-300 px-3 py-2"
                              type="text"
                              name="First name"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#0A2540] mb-1">
                              Last name <span className="text-red-600">*</span>
                            </label>
                            <input
                              className="w-full rounded-md border border-slate-300 px-3 py-2"
                              type="text"
                              name="Last name"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-[#0A2540] mb-1">
                            Phone <span className="text-red-600">*</span>
                          </label>
                          <input
                            className="w-full rounded-md border border-slate-300 px-3 py-2"
                            type="tel"
                            name="Phone"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-[#0A2540] mb-1">
                            Email <span className="text-red-600">*</span>
                          </label>
                          <input
                            className="w-full rounded-md border border-slate-300 px-3 py-2"
                            type="email"
                            name="Email"
                            required
                          />
                        </div>

                        {/* Extra fields — kept exactly as required */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-[#0A2540] mb-1">DOB</label>
                            <input
                              className="w-full rounded-md border border-slate-300 px-3 py-2"
                              type="date"
                              name="dob"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#0A2540] mb-1">City</label>
                            <input
                              className="w-full rounded-md border border-slate-300 px-3 py-2"
                              type="text"
                              name="city"
                              placeholder="City"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#0A2540] mb-1">
                              State <span className="text-red-600">*</span>
                            </label>
                            <input
                              className="w-full rounded-md border border-slate-300 px-3 py-2"
                              type="text"
                              name="state"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#0A2540] mb-1">
                              Zipcode
                            </label>
                            <input
                              className="w-full rounded-md border border-slate-300 px-3 py-2"
                              type="text"
                              name="zipcode"
                            />
                          </div>
                        </div>

                        {/* Hidden fields */}
                        <input type="hidden" name="Case type" value={title} />
                        <input type="hidden" name="page source" value={pageSource} />

                        {/* TrustedForm — FIXED with key={step} */}
                        <input type="hidden" name="trusteformcerturl" />
                        <input type="hidden" name="trustedformtoken" />
                        <input type="hidden" name="trustedformpingurl" />
                        
                        {step === "contact" && (
                          <TrustedFormCapture
                            key={step}
                            certField="trusteformcerturl"
                            tokenField="trustedformtoken"
                            pingField="trustedformpingurl"
                          />
                        )}

                        {/* Consent */}
                        <label className="flex items-start gap-2 text-[12px] text-slate-600">
                          <input type="checkbox" required className="mt-1" />
                          <span>
                            By submitting "Yes", you provide your electronic signature and agree to be called and/or contacted 
                            at the number provided above by MassTortPlaintiffs and its marketing partners about our services, 
                            which may include autodialed calls, prerecorded voice messages, SMS text messages, and ringless voicemail, 
                            even if your telephone number is currently listed on any State, Federal, or corporate Do Not Call registry. 
                            Your consent is not required to purchase any goods or services. You also agree to our Terms of Use, 
                            Privacy Policy, No Legal Advice Disclaimer, and Cookie Policy. Message and data rates may apply. 
                            Message frequency varies.
                          </span>
                        </label>

                        <button
                          type="submit"
                          disabled={submitting}
                          className="w-full h-12 rounded-md bg-green-600 text-white font-semibold"
                        >
                          {submitting ? "Submitting..." : "SUBMIT"}
                        </button>
                      </div>
                    </div>
                  </form>

                ) : (
                  
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setStep("contact");
                    }}
                    className="mt-4"
                  >
                    <div className="rounded-lg overflow-hidden border border-slate-200">
                      <div className="bg-[#C89B3C] px-4 py-3 text-center">
                        <p className="text-[#0A2540] text-xl md:text-2xl font-extrabold">
                          {cfg.bannerTitle}
                        </p>
                        <p className="text-[#0A2540] text-sm md:text-[13px] mt-1">
                          {cfg.bannerSubtitle}
                        </p>
                      </div>

                      <div className="p-4 md:p-5 space-y-5 bg-[#FAF7F0]">
                        {cfg.quiz.map((f) => renderField(f))}
                        <button
                          type="submit"
                          className="w-full h-12 rounded-md bg-green-600 text-white font-semibold"
                        >
                          {cfg.cta || "NEXT"}
                        </button>
                      </div>
                    </div>
                  </form>

                )}

              </div>
            </div>

          </div>
        </section>
      </main>
    </>
  );
}
